import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Button } from '@/components/ui/button';
import { Bold, Italic, List, ListOrdered, Heading2 } from 'lucide-react';
import { FIELD_CHROME } from '@/lib/field-chrome';
import axiosInstance from '@/lib/axios';
import { MentionChip } from '@/components/mentions/mention-chip';
import { MentionMenu } from '@/components/mentions/MentionMenu';
import { mentionToken, type MentionSuggestion } from '@/components/mentions/mention-state';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  communityId?: string;
}

interface MentionQuery {
  trigger: 'at' | 'hash';
  query: string;
  from: number;
  to: number;
}

function queryBeforeCursor(editor: Editor): MentionQuery | null {
  const { from } = editor.state.selection;
  const text = editor.state.doc.textBetween(Math.max(0, from - 80), from, '\n', '\n');
  const match = text.match(/(?:^|\s)([@#])([^\s@#]*)$/);
  if (!match) return null;
  return {
    trigger: match[1] === '#' ? 'hash' : 'at',
    query: match[2],
    from: from - match[1].length - match[2].length,
    to: from,
  };
}

function mentionHtml(item: MentionSuggestion) {
  const token = mentionToken(item)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
  const label = `${item.trigger === 'at' ? '@' : '#'}${item.label}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;');
  return `<strong data-token="${token}">${label}</strong> `;
}

export function RichTextEditor({ value, onChange, communityId }: RichTextEditorProps) {
  const [items, setItems] = useState<MentionSuggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [menuTop, setMenuTop] = useState<number | null>(null);
  const [menuHeight, setMenuHeight] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef(items);
  const activeRef = useRef(activeIndex);
  const openRef = useRef(open);
  const queryRef = useRef<MentionQuery | null>(null);
  const requestIdRef = useRef(0);
  const fetchTimerRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    itemsRef.current = items;
    activeRef.current = activeIndex;
    openRef.current = open;
  }, [items, activeIndex, open]);

  const editor = useEditor({
    extensions: communityId ? [StarterKit, MentionChip] : [StarterKit],
    content: value,
    onUpdate: ({ editor: current }) => {
      onChange(current.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm dark:prose-invert max-w-none min-h-[150px] w-full bg-transparent px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none dark:text-neutral-100',
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  useEffect(() => {
    if (!editor || !communityId) return;

    const sync = () => {
      const found = queryBeforeCursor(editor);
      queryRef.current = found;
      if (fetchTimerRef.current !== null) window.clearTimeout(fetchTimerRef.current);
      if (!found) {
        requestIdRef.current += 1;
        setOpen(false);
        setItems([]);
        setMenuTop(null);
        return;
      }
      const content = contentRef.current;
      if (content) {
        const coords = editor.view.coordsAtPos(found.to);
        const rect = content.getBoundingClientRect();
        setMenuTop(coords.bottom - rect.top + 4);
      }
      setOpen(true);
      const requestId = ++requestIdRef.current;
      fetchTimerRef.current = window.setTimeout(async () => {
        if (requestId !== requestIdRef.current) return;
        try {
          const { data } = await axiosInstance.get(`/api/communities/${communityId}/mentions`, {
            params: { trigger: found.trigger, q: found.query },
          });
          if (requestId !== requestIdRef.current) return;
          const next = Array.isArray(data?.items) ? (data.items as MentionSuggestion[]) : [];
          setItems(next);
          setActiveIndex(0);
        } catch {
          if (requestId === requestIdRef.current) setItems([]);
        }
      }, 200);
    };

    const insert = (item: MentionSuggestion) => {
      const found = queryRef.current;
      if (!found) return;
      editor
        .chain()
        .focus()
        .insertContentAt({ from: found.from, to: found.to }, mentionHtml(item))
        .run();
      setOpen(false);
      setItems([]);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (!openRef.current || itemsRef.current.length === 0) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((index) => (index + 1) % itemsRef.current.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((index) => (index - 1 + itemsRef.current.length) % itemsRef.current.length);
      } else if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        insert(itemsRef.current[activeRef.current]);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
      }
    };

    editor.on('update', sync);
    editor.on('selectionUpdate', sync);
    editor.view.dom.addEventListener('keydown', onKeyDown, true);
    return () => {
      if (fetchTimerRef.current !== null) window.clearTimeout(fetchTimerRef.current);
      editor.off('update', sync);
      editor.off('selectionUpdate', sync);
      editor.view.dom.removeEventListener('keydown', onKeyDown, true);
    };
  }, [editor, communityId]);

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const observer = new ResizeObserver(() => {
      const next = menu.offsetHeight;
      setMenuHeight((current) => (current === next ? current : next));
    });
    observer.observe(menu);
    return () => observer.disconnect();
  }, [open, items, menuTop]);

  if (!editor) {
    return null;
  }

  return (
    <div
      className={`flex flex-col rounded-xl ${FIELD_CHROME.solid.base} ${FIELD_CHROME.solid.focusWithin}`}
    >
      <div className="flex gap-1 border-b border-zinc-300 px-2 py-1.5 dark:border-zinc-700">
        <Button
          type="button"
          variant={editor.isActive('bold') ? 'secondary' : 'ghost'}
          size="icon"
          className="h-8 w-8"
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive('italic') ? 'secondary' : 'ghost'}
          size="icon"
          className="h-8 w-8"
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive('heading', { level: 2 }) ? 'secondary' : 'ghost'}
          size="icon"
          className="h-8 w-8"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive('bulletList') ? 'secondary' : 'ghost'}
          size="icon"
          className="h-8 w-8"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive('orderedList') ? 'secondary' : 'ghost'}
          size="icon"
          className="h-8 w-8"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
      </div>
      <div
        ref={contentRef}
        className="relative [&_ul]:list-disc [&_ol]:list-decimal [&_li]:ml-4"
        style={
          open && menuTop !== null
            ? { minHeight: menuTop + (menuHeight || Math.min(items.length * 48, 240)) + 8 }
            : undefined
        }
      >
        <EditorContent editor={editor} />
        {open && items.length > 0 && menuTop !== null ? (
          <div ref={menuRef} className="absolute left-2 right-2 z-20" style={{ top: menuTop }}>
            <MentionMenu
              items={items}
              activeIndex={activeIndex}
              onSelect={(item) => {
                const found = queryRef.current;
                if (!found) return;
                editor
                  .chain()
                  .focus()
                  .insertContentAt({ from: found.from, to: found.to }, mentionHtml(item))
                  .run();
                setOpen(false);
                setItems([]);
                setMenuTop(null);
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
